'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { getTopicData, TopicModal } from './TopicModal';

/** Topics that navigate to a dedicated page instead of opening a modal */
const TOPIC_ROUTES = {
    warranty: '/warranty-support',
};

/* ─── Single topic card ─────────────────────────────────────────────────────── */
const TopicCard = ({ topic, onClick }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <div
            className="col-12 md:col-6 lg:col-4 p-2"
            style={{ cursor: 'pointer' }}
            onClick={() => onClick(topic.id)}
        >
            <div
                className="h-full border-round-xl overflow-hidden"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    background: 'var(--surface-card)',
                    border: `1.5px solid ${hovered ? topic.color : 'var(--surface-border)'}`,
                    boxShadow: hovered
                        ? `0 8px 32px ${topic.color}25`
                        : '0 1px 6px rgba(0,0,0,0.06)',
                    transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
                    transition: 'all 0.25s ease',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Colored top strip */}
                <div style={{ height: 4, background: topic.gradient }} />

                <div className="flex flex-column h-full p-4 gap-3">
                    {/* Icon + title row */}
                    <div className="flex align-items-center gap-3">
                        <div
                            className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                            style={{
                                width: 48, height: 48,
                                background: hovered ? topic.gradient : topic.bg,
                                border: `1.5px solid ${topic.color}30`,
                                transition: 'background 0.25s',
                                boxShadow: hovered ? `0 4px 14px ${topic.color}40` : 'none',
                            }}
                        >
                            <i
                                className={topic.icon}
                                style={{
                                    fontSize: '1.25rem',
                                    color: hovered ? '#fff' : topic.color,
                                    transition: 'color 0.25s',
                                }}
                            />
                        </div>

                        <div className="flex-1 min-w-0">
                            <p
                                className="m-0 font-bold"
                                style={{
                                    fontSize: '0.95rem',
                                    color: hovered ? topic.color : 'var(--text-color)',
                                    transition: 'color 0.25s',
                                }}
                            >
                                {topic.title}
                            </p>
                            <p className="m-0 mt-1 text-500" style={{ fontSize: '0.72rem' }}>
                                {topic.faqs.length} FAQs
                            </p>
                        </div>
                    </div>

                    {/* Subtitle */}
                    <p className="m-0 text-600 flex-1" style={{ fontSize: '0.83rem', lineHeight: 1.65 }}>
                        {topic.subtitle}
                    </p>

                    {/* Footer action row */}
                    <div
                        className="flex align-items-center justify-content-between mt-auto pt-3"
                        style={{ borderTop: `1px solid ${hovered ? topic.color + '25' : 'var(--surface-border)'}`, transition: 'border-color 0.25s' }}
                    >
                        <span
                            className="text-xs font-semibold"
                            style={{ color: topic.color }}
                        >
                            View answers
                        </span>
                        <div
                            className="flex align-items-center justify-content-center border-circle"
                            style={{
                                width: 28, height: 28,
                                background: hovered ? topic.gradient : topic.bg,
                                transition: 'background 0.25s',
                            }}
                        >
                            <i
                                className="pi pi-arrow-right"
                                style={{
                                    fontSize: '0.65rem',
                                    color: hovered ? '#fff' : topic.color,
                                    transition: 'color 0.25s',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ─── Grid + Modal orchestrator ─────────────────────────────────────────────── */
export const TopicGrid = ({ replacementAllow = 7 }) => {
    const router = useRouter();
    const [activeTopic, setActiveTopic] = useState(null);
    const topicData = getTopicData(replacementAllow);
    const TOPICS = [
        topicData.orders,
        topicData.returns,
        topicData.warranty,
        topicData.installation,
        topicData.app,
        topicData.products,
    ];

    const handleTopicClick = (id) => {
        if (TOPIC_ROUTES[id]) {
            router.push(TOPIC_ROUTES[id]);
        } else {
            setActiveTopic(id);
        }
    };

    return (
        <>
            <section className="container" style={{ padding: '1rem 1rem 2rem' }}>
                {/* Grid */}
                <div className="grid">
                    {TOPICS.map((topic) => (
                        <TopicCard
                            key={topic.id}
                            topic={topic}
                            onClick={handleTopicClick}
                        />
                    ))}
                </div>
            </section>

            {/* Modal — only for topics without a dedicated page */}
            <TopicModal
                topic={activeTopic}
                visible={!!activeTopic}
                onHide={() => setActiveTopic(null)}
                replacementAllow={replacementAllow}
            />
        </>
    );
};
